import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { CustomerSelect } from '@/components/selects';
import { financeApi } from '@/api/finance.api';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { showApiError } from '@/utils/apiError';
import type { FinPayment, FinVendor } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinancePaymentsPage() {
  const { hasAny } = usePermissions();
  const canRecord = hasAny('FINANCE_PAYMENT_MANAGE', 'FINANCE_MANAGE');
  const pagination = usePagination();
  const [payments, setPayments] = useState<FinPayment[]>([]);
  const [vendors, setVendors] = useState<FinVendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await financeApi.listPayments({ page: pagination.page, size: pagination.size });
      setPayments(r.content);
      pagination.applyPageResponse(r);
    } catch (e) {
      showApiError(e, 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
    financeApi.listApVendors().then(setVendors).catch(() => setVendors([]));
  }, [load]);

  const recordPayment = async (e: FormEvent) => {
    e.preventDefault();
    const cid = Number(customerId);
    const amt = Number(amount);
    if (!cid || Number.isNaN(amt) || amt <= 0) {
      notify.error('Select a customer and enter a valid amount');
      return;
    }
    setRecording(true);
    try {
      await financeApi.recordPayment({ customerId: cid, amount: amt, paymentMethod: 'BANK' });
      notify.success('Payment recorded');
      setCustomerId('');
      setAmount('');
      void load();
    } catch (err) {
      showApiError(err, 'Failed to record payment');
    } finally {
      setRecording(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        description="Customer receipts (AR) and accounts payable vendor directory"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Payments' }]}
      />

      {canRecord && (
        <form className="card p-4 mb-4 max-w-md space-y-3" onSubmit={recordPayment}>
          <h3 className="font-semibold">Record customer payment</h3>
          <CustomerSelect value={customerId} onChange={setCustomerId} required />
          <Input label="Amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <Button type="submit" loading={recording}>
            Post payment
          </Button>
        </form>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Recent payments</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Number</th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-left">Method</th>
                <th className="p-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="p-2 font-mono">{p.paymentNumber}</td>
                  <td className="p-2">{p.paymentDate}</td>
                  <td className="p-2 text-right font-mono">{p.amount}</td>
                  <td className="p-2">{p.paymentMethod ?? '—'}</td>
                  <td className="p-2">{p.statusCode}</td>
                </tr>
              ))}
              {payments.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-slate-500">
                    No payments recorded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <TablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalElements={pagination.totalElements}
            onPageChange={pagination.setPage}
            loading={loading}
            hasRows={payments.length > 0}
          />
        </div>

        <div className="card p-4">
          <h3 className="font-semibold mb-2">AP vendors</h3>
          <p className="text-xs text-slate-500 mb-3">
            Vendor bills from Procurement post AP journals. Batch payment runs are roadmap.
          </p>
          <ul className="text-sm space-y-1 max-h-64 overflow-y-auto">
            {vendors.map((v) => (
              <li key={v.id}>
                <span className="font-mono text-xs">{v.vendorCode}</span> — {v.vendorName}
                {!v.active && <span className="text-slate-400 ml-1">(inactive)</span>}
              </li>
            ))}
            {vendors.length === 0 && <li className="text-slate-500">No finance vendors — sync from procurement setup</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
